<template>
  <section>

    <!-- ── Add / Edit Service Form ── -->
    <div class="card shadow-sm form-card mb-4">
      <div class="form-card-header">
        <div class="section-number">
          <i class="fe" :class="edit ? 'fe-edit-2' : 'fe-plus'" style="font-size: 12px;"></i>
        </div>
        <h6 class="mb-0 font-weight-bold text-white">{{ edit ? 'Update Service' : 'Add New Service' }}</h6>
        <span v-if="edit" class="badge badge-warning ml-auto" style="border-radius: 20px; font-size: 0.75rem;">
          Editing mode
        </span>
      </div>

      <div class="card-body px-4 py-4">
        <div class="form-row">
          <div class="form-group col-md-3">
            <label class="small font-weight-bold text-secondary">Service Category</label>
            <select class="form-control" v-model="register.service_category_id" @change="getCategories">
              <option value="" disabled>Select category…</option>
              <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                {{ cat.category_name }}
              </option>
            </select>
          </div>
          <div class="form-group col-md-2">
            <label for="service-code" class="small font-weight-bold text-secondary">Service Code</label>
            <input id="service-code" type="text" maxlength="191" class="form-control" placeholder="e.g. SRV-001"
              v-model.trim="register.service_code" />
          </div>
          <div class="form-group col-md-4">
            <label class="small font-weight-bold text-secondary">Description <span class="text-danger">*</span></label>
            <input type="text" required class="form-control" placeholder="e.g. Abdominal Pelvic Scan"
              v-model="register.description" />
          </div>
          <div class="form-group col-md-3">
            <label class="small font-weight-bold text-secondary">Price (₦) <span class="text-danger">*</span></label>
            <div class="input-group">
              <div class="input-group-prepend">
                <span class="input-group-text">₦</span>
              </div>
              <input type="number" min="0" required class="form-control" placeholder="0.00" v-model="register.price" />
            </div>
          </div>
        </div>

        <div class="d-flex" style="gap: 8px;">
          <button @click="submitForm" class="btn btn-submit">
            <i class="fe mr-1" :class="edit ? 'fe-save' : 'fe-plus'"></i>
            {{ edit ? 'Update Service' : 'Add Service' }}
          </button>
          <button v-if="edit" type="button" class="btn btn-outline-secondary" @click="edit = false; clearIt()">
            <i class="fe fe-x mr-1"></i> Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- ── Services Table ── -->
    <div class="card shadow-sm table-card mb-4">
      <div class="d-flex flex-wrap align-items-center justify-content-between px-4 pt-4 pb-3" style="gap: 8px;">
        <div class="d-flex align-items-center">
          <i class="fe fe-activity text-primary mr-2"></i>
          <h6 class="mb-0 font-weight-bold">Service List</h6>
          <span v-if="services && services.data" class="badge badge-soft-info ml-2">{{ activeFilters ? shownCount + ' of ' + totalCount : totalCount }}</span>
        </div>
        <button type="button" class="btn btn-sm ml-auto" :class="showFilters || activeFilters ? 'btn-secondary' : 'btn-outline-secondary'"
          :aria-expanded="showFilters ? 'true' : 'false'" @click="showFilters = !showFilters">
          <i class="fe fe-filter mr-1"></i> Filters<span v-if="activeFilters" class="badge badge-light ml-1">{{ activeFilters }}</span>
        </button>
        <div class="btn-group btn-group-sm mr-2" role="group" aria-label="Tariff">
          <button type="button" class="btn" :class="tariff === 'agency' ? 'btn-primary' : 'btn-outline-primary'"
            @click="setTariff('agency')">Agency tariff</button>
          <button type="button" class="btn" :class="tariff === 'national' ? 'btn-primary' : 'btn-outline-primary'"
            @click="setTariff('national')">National (NHIA) tariff</button>
        </div>
        <button type="button" class="btn btn-sm btn-outline-success" :disabled="!filteredServices.length"
          @click="exportExcel">
          <i class="fe fe-download mr-1"></i> Download Excel
        </button>
      </div>

      <div v-show="showFilters" class="filter-panel mx-4 mb-3 p-3">
        <div class="form-row">
          <div class="form-group col-md-6 mb-2">
            <label for="service-f-q" class="small font-weight-bold text-secondary mb-1">Search</label>
            <input id="service-f-q" type="search" class="form-control form-control-sm" placeholder="Description or code…"
              v-model.trim="filters.q" />
          </div>
          <div class="form-group col-md-3 col-6 mb-2">
            <label for="service-f-cat" class="small font-weight-bold text-secondary mb-1">Category</label>
            <select id="service-f-cat" class="form-control form-control-sm" v-model="filters.category">
              <option value="">All categories</option>
              <option v-for="o in filterOptions.categories" :key="o" :value="o">{{ o }}</option>
            </select>
          </div>
          <div class="form-group col-md-3 col-6 mb-2">
            <label for="service-f-prog" class="small font-weight-bold text-secondary mb-1">Programme</label>
            <select id="service-f-prog" class="form-control form-control-sm" v-model="filters.programme">
              <option value="">All programmes</option>
              <option value="__none">No programme</option>
              <option v-for="o in filterOptions.programmes" :key="o" :value="o">{{ o }}</option>
            </select>
          </div>
          <div class="form-group col-md-2 col-6 mb-2">
            <label for="service-f-inv" class="small font-weight-bold text-secondary mb-1">Investigation</label>
            <select id="service-f-inv" class="form-control form-control-sm" v-model="filters.investigation">
              <option value="">Any</option>
              <option value="yes">Investigations only</option>
              <option value="no">Not investigations</option>
            </select>
          </div>
          <div class="form-group col-md-2 col-6 mb-2">
            <label for="service-f-min" class="small font-weight-bold text-secondary mb-1">Price from (₦)</label>
            <input id="service-f-min" type="number" min="0" class="form-control form-control-sm" v-model="filters.min" />
          </div>
          <div class="form-group col-md-2 col-6 mb-2">
            <label for="service-f-max" class="small font-weight-bold text-secondary mb-1">Price to (₦)</label>
            <input id="service-f-max" type="number" min="0" class="form-control form-control-sm" v-model="filters.max" />
          </div>
          <div class="form-group col-md-2 col-6 mb-2">
            <label for="service-f-code" class="small font-weight-bold text-secondary mb-1">Code</label>
            <select id="service-f-code" class="form-control form-control-sm" v-model="filters.code">
              <option value="">Any</option>
              <option value="with">With a code</option>
              <option value="without">Without a code</option>
            </select>
          </div>
        </div>
        <div class="d-flex align-items-center" style="gap: 8px;">
          <small class="text-muted">Showing {{ shownCount }} of {{ totalCount }}</small>
          <button type="button" class="btn btn-sm btn-link ml-auto" :disabled="!activeFilters" @click="clearFilters">Clear filters</button>
        </div>
      </div>

      <p v-if="tariff === 'national'" class="small text-muted px-4 mb-2">
        <i class="fe fe-info mr-1"></i> The national (NHIA) tariff, used for BHCPF and UNICEF enrollees. It is read-only here.
      </p>

      <div class="table-responsive">
        <table class="table align-td-middle mb-0">
          <thead style="background: #f8fafc;">
            <tr>
              <th class="table-th">#</th>
              <th class="table-th">Code</th>
              <th class="table-th">Category</th>
              <th class="table-th">Description</th>
              <th class="table-th">Price</th>
              <th class="table-th"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(service, index) in filteredServices" :key="service.id" class="table-row">
              <td class="px-4 py-3 text-muted small">{{ index + 1 }}</td>
              <td class="py-3"><span v-if="service.service_code" class="code-chip">{{ service.service_code }}</span><span v-else class="text-muted">—</span></td>
              <td class="py-3">
                <span v-if="service.category">{{ service.category.category_name }}</span>
                <span v-else class="text-muted">—</span>
              </td>
              <td class="py-3 font-weight-bold"><router-link :to="'/price-list/service/' + service.id" title="See how it is used">{{ service.description }}</router-link> <span class="btn btn-outline-info btn-sm" v-for="sector in service.sectors" :key="sector"> {{sector.name}} </span> </td>
              <td class="py-3 font-weight-bold">&#8358;{{ service.price | numeral("0,0.00") }}</td>
              <td class="py-3">
                <div v-if="tariff === 'agency'" class="d-flex" style="gap: 6px;">
                  <button class="btn btn-sm btn-outline-info" @click="editService(service)" title="Edit">
                    <i class="fe fe-edit-2"></i>
                  </button>
                  <button class="btn btn-sm btn-outline-danger" @click="deleteService(service)" title="Delete">
                    <i class="fe fe-trash-2"></i>
                  </button>
                </div>
              </td>
            </tr>
            <tr v-if="!filteredServices.length">
              <td colspan="6" class="text-center py-5 text-muted">
                <i class="fe fe-inbox" style="font-size: 2rem; opacity: 0.3;"></i>
                <p class="mt-2 mb-0">{{ totalCount && activeFilters ? 'No services match the filters' : 'No services found' }}</p>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="vld-parent">
      <loading :active.sync="isLoading" loader="dots" :can-cancel="true" :is-full-page="fullPage"></loading>
    </div>
  </section>
</template>

<script>
import Loading from "vue-loading-overlay";
import "vue-loading-overlay/dist/vue-loading.css";
import { downloadSheet, datedFileName } from "@/service/sheetExport";

let agency = JSON.parse(localStorage.getItem("agency"));

export default {
  components: { Loading },
  data() {
    return {
      showFilters: false,
      filters: { q: "", category: "", programme: "", investigation: "", min: "", max: "", code: "" },
      tariff: "agency", // "agency" = this agency's price list; "national" = the NHIA tariff (catalog of agency 1)
      user: null,
      categories: "",
      services: "",
      sector_add: "",
      edit: false,
      show: false,
      isLoading: false,
      fullPage: true,
      service_id: "",
      sectors: [
        "Basic Healthcare Provision Fund",
        "State Equity Program",
        "Voluntary Contributor",
        "Civil Servant",
        "Organized Private Sector Plan",
        "Tertiary Student Health Insurance Plan (T-SHIP)",
      ],
      register: {
        service_category_id: "",
        service_code: "",
        price: "",
        description: "",
        sector: "",
      },
    };
  },
  beforeMount() {
    this.user = JSON.parse(localStorage.getItem("user"));
    this.axios
      .get(`/api/v1/auth/service_category`)
      .then((response) => { this.categories = response.data.data; })
      .catch((error) => { console.error(error); });
  },
  computed: {
    // Filters run on the loaded list (the endpoint returns the whole tariff); choices come from that list.
    filteredServices() {
      const rows = (this.services && this.services.data) || [];
      const f = this.filters;
      const q = f.q.toLowerCase();
      const min = f.min === "" ? null : Number(f.min);
      const max = f.max === "" ? null : Number(f.max);
      return rows.filter((s) => {
        if (q && ![s.description, s.service_code].some((v) => String(v || "").toLowerCase().includes(q))) return false;
        if (f.category && (s.category ? s.category.category_name : "") !== f.category) return false;
        if (f.programme === "__none" && (s.sectors || []).length) return false;
        if (f.programme && f.programme !== "__none" && !(s.sectors || []).some((x) => x.name === f.programme)) return false;
        if (f.investigation === "yes" && !Number(s.is_investigation)) return false;
        if (f.investigation === "no" && Number(s.is_investigation)) return false;
        if (min !== null && Number(s.price) < min) return false;
        if (max !== null && Number(s.price) > max) return false;
        if (f.code === "with" && !s.service_code) return false;
        if (f.code === "without" && s.service_code) return false;
        return true;
      });
    },
    filterOptions() {
      const rows = (this.services && this.services.data) || [];
      const uniq = (vals) => [...new Set(vals.map((v) => String(v || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
      return {
        categories: uniq(rows.map((s) => s.category && s.category.category_name)),
        programmes: uniq([].concat(...rows.map((s) => (s.sectors || []).map((x) => x.name)))),
      };
    },
    shownCount() {
      return this.filteredServices.length;
    },
    totalCount() {
      return ((this.services && this.services.data) || []).length;
    },
    activeFilters() {
      return Object.keys(this.filters).filter((k) => this.filters[k] !== "").length;
    },
    catalogId() {
      return this.tariff === "national" ? 1 : agency.id;
    },
  },
  methods: {
    clearFilters() {
      Object.keys(this.filters).forEach((k) => { this.filters[k] = ""; });
    },
    setTariff(tariff) {
      if (this.tariff === tariff) return;
      this.tariff = tariff;
      if (this.edit) { this.edit = false; this.clearIt(); }
      if ("current_page" in this.$data) this.current_page = 1;
      this.getServices();
    },
    addSector(service, sector) {
      this.axios
        .post(`/api/v1/auth/service-drug-sector`, { name: sector, service_id: service.id })
        .then((response) => {
          console.log(response);
          this.getServices();
          this.$toasted.success("Added Successfully!", { position: "top-left", duration: 5000 });
        })
        .catch((error) => { console.error(error); });
    },
    // The endpoint returns the whole price list, so the sheet has every service on the page.
    exportExcel() {
      downloadSheet(this.filteredServices, [
        { label: "S/N", value: (s, i) => i + 1 },
        { label: "Category", value: (s) => (s.category ? s.category.category_name : "") },
        { label: "Description", value: (s) => s.description },
        { label: "Service Code", value: (s) => s.service_code },
        { label: "Price (₦)", value: (s) => (s.price === null || s.price === "" ? "" : Number(s.price)) },
        { label: "Programmes", value: (s) => (s.sectors || []).map((x) => x.name).join(", ") },
      ], datedFileName(this.tariff === "national" ? "National (NHIA) service tariff" : "Service price list"), "Services");
    },
    submitForm() {
      if (this.edit == false) {
        this.addService();
      } else {
        this.updateService();
      }
    },
    deleteService(service) {
      if (confirm("Are you sure you want to delete this service?")) {
        this.axios
          .delete(`/api/v1/auth/services/${service.id}`)
          .then((response) => {
            console.log(response);
            this.getServices();
            this.$toasted.success("Deleted Successfully!", { position: "top-left", duration: 5000 });
          })
          .catch((error) => { console.error(error); });
      }
    },
    deleteSector(sector) {
      if (confirm("Are you sure you want to delete this?")) {
        this.axios
          .delete(`/api/v1/auth/servicedrugsector/${sector.id}`)
          .then((response) => {
            console.log(response);
            this.getServices();
            this.$toasted.success("Deleted Successfully!", { position: "top-left", duration: 5000 });
          })
          .catch((error) => { console.error(error); });
      }
    },
    getCategories() {
      this.axios
        .get(`/api/v1/auth/service_category`)
        .then((response) => { this.categories = response.data.data; })
        .catch((error) => { console.error(error); });
    },
    getServices() {
      this.axios
        .get(`/api/v1/auth/service-agency/${this.catalogId}`)
        .then((response) => { this.services = response.data; })
        .catch((error) => { console.error(error); });
    },
    editService(service) {
      this.edit = true;
      this.register.service_category_id = service.service_category_id;
      this.register.price = service.price;
      this.register.description = service.description;
      this.register.service_code = service.service_code || "";
      this.service_id = service.id;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    addService() {
      this.isLoading = true;
      this.user = JSON.parse(localStorage.getItem("user"));
      this.axios
        .post("/api/v1/auth/services", {
          agency_id: agency.id,
          service_category_id: this.register.service_category_id,
          price: this.register.price,
          service_code: this.register.service_code || null,
          description: this.register.description,
          sector: this.register.sector,
        })
        .then((response) => {
          console.log(response);
          this.isLoading = false;
          this.$toasted.success("Added Successfully!", { position: "top-center", duration: 2000 });
          this.getServices();
          this.clearIt();
        })
        .catch((error) => {
          console.log(error.response);
          this.isLoading = false;
          this.$toasted.error("Not Successful", { position: "top-left", duration: 5000 });
        });
    },
    updateService() {
      this.isLoading = true;
      this.user = JSON.parse(localStorage.getItem("user"));
      this.axios
        .put(`/api/v1/auth/services/${this.service_id}`, {
          agency_id: agency.id,
          service_category_id: this.register.service_category_id,
          price: this.register.price,
          service_code: this.register.service_code || null,
          description: this.register.description,
        })
        .then((response) => {
          console.log(response);
          this.isLoading = false;
          this.edit = false;
          this.getServices();
          this.$toasted.success("Updated Successfully!", { position: "top-center", duration: 2000 });
        })
        .catch((error) => {
          console.log(error.response);
          this.isLoading = false;
          this.$toasted.error("Not Successful", { position: "top-left", duration: 5000 });
        });
    },
    clearIt() {
      this.register.description = "";
      this.register.service_code = "";
      this.register.price = "";
      this.register.service_category_id = "";
    },
  },
  created() {
    this.getCategories();
    this.getServices();
  },
};
</script>

<style scoped>
.form-card {
  border-radius: 12px;
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.form-card-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 20px;
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
}

.section-number {
  width: 28px;
  height: 28px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}

.table-card {
  border-radius: 12px;
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.table-th {
  font-size: 0.73rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
  font-weight: 600;
  border-bottom: 2px solid #e2e8f0;
  padding: 12px 16px;
}

.table-row:hover {
  background: #f8fafc;
}

.btn-submit {
  background: linear-gradient(135deg, #1e3a5f 0%, #2563eb 100%);
  border: none;
  color: #fff;
  border-radius: 8px;
  font-weight: 600;
  padding: 8px 24px;
}

.btn-submit:hover {
  opacity: 0.9;
  color: #fff;
}

.form-control:focus {
  border-color: #2563eb;
  box-shadow: 0 0 0 0.15rem rgba(37, 99, 235, 0.15);
}
.filter-panel {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}
</style>
